// An action's settings with every UI hint filled in. The server sends them
// resolved (our default for every field, the action's hints, the deployment's
// labels) and cascaded (its defaults, ranges and locks, the workspace's); this
// fills anything it left out with the same defaults, so the form never meets
// a setting it cannot draw (plan, "UI: modular").

import type { ControlKind, ActionSetting, ResolvedSetting, SettingsMode, SettingUi } from '../types';

const CONTROLS: ReadonlySet<ControlKind> = new Set(['number', 'slider', 'stepper', 'switch', 'select', 'text']);
const MODES: SettingsMode[] = ['workspace', 'run'];

/** `blur_threshold` → "Blur threshold". */
export function readableKey(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1).toLowerCase() : key;
}

/** Our default control: enum → select; boolean → switch; integer → stepper;
 *  number with a range → slider; number → number input; string → text. */
export function defaultControl(s: Pick<ActionSetting, 'type' | 'enum' | 'minimum' | 'maximum'>): ControlKind {
  if (Array.isArray(s.enum) && s.enum.length > 0) return 'select';
  switch (s.type) {
    case 'boolean':
      return 'switch';
    case 'integer':
      return 'stepper';
    case 'number':
      return s.minimum != null && s.maximum != null ? 'slider' : 'number';
    default:
      return 'text';
  }
}

/** The control a setting is drawn with: its own, when it is one we have and fits it; else ours. */
function controlOf(s: ActionSetting): ControlKind {
  const asked = s.ui?.control;
  if (!asked || !CONTROLS.has(asked)) return defaultControl(s);
  if (asked === 'select' && !(Array.isArray(s.enum) && s.enum.length > 0)) return defaultControl(s);
  if (asked === 'switch' && s.type !== 'boolean') return defaultControl(s);
  if ((asked === 'slider' || asked === 'stepper' || asked === 'number') && s.type !== 'number' && s.type !== 'integer') {
    return defaultControl(s);
  }
  return asked;
}

export function resolveSetting(key: string, s: ActionSetting, index: number): ResolvedSetting {
  const ui = s.ui ?? {};
  const editable = Array.isArray(ui.editable) ? ui.editable.filter((m): m is SettingsMode => MODES.includes(m)) : MODES;
  // A resolved `ui.title` that is only the key made readable is our default,
  // and the setting's own title says more.
  const readable = readableKey(key);
  const resolved: SettingUi = {
    control: controlOf(s),
    title: (ui.title && ui.title !== readable ? ui.title : null) || s.title || ui.title || readable,
    group: ui.group || 'Settings',
    order: typeof ui.order === 'number' && Number.isFinite(ui.order) ? ui.order : index,
    unit: ui.unit ?? null,
    description: ui.description ?? s.description ?? null,
    hint: ui.hint ?? ui.description ?? s.description ?? null,
    link: ui.link ?? null,
    advanced: ui.advanced === true,
    editable,
  };
  return { ...s, key, ui: resolved };
}

/** Every setting resolved, in display order (order, then as declared). */
export function resolveSettings(settings: Record<string, ActionSetting> | null | undefined): ResolvedSetting[] {
  return Object.entries(settings ?? {})
    .map(([key, s], i) => ({ s: resolveSetting(key, s, i), i }))
    .sort((a, b) => a.s.ui.order - b.s.ui.order || a.i - b.i)
    .map((x) => x.s);
}

/** May a form in this mode change the setting? The rest are shown, locked, with who set them. */
export function canChange(s: Pick<ResolvedSetting, 'ui'>, mode: SettingsMode): boolean {
  return s.ui.editable.includes(mode);
}

/**
 * Who set a setting this form cannot change, in words, or null for nothing
 * said. Only a lock the workspace chose is named, when running. A setting
 * fixed by the action or the deployment is shown as it is, unchangeable, with
 * no word about who: the screens state values and ranges as facts and never
 * name TileRiver as the one limiting them (decided 2026-10-04).
 */
export function lockNote(s: Pick<ResolvedSetting, 'lockedBy'>, mode: SettingsMode): string | null {
  return mode === 'run' && s.lockedBy === 'workspace' ? 'Set by your workspace' : null;
}

/** The settings a form in this mode can change. */
export function changeableSettings(settings: Record<string, ActionSetting> | null | undefined, mode: SettingsMode): ResolvedSetting[] {
  return resolveSettings(settings).filter((s) => canChange(s, mode));
}

export interface SettingGroup {
  title: string;
  settings: ResolvedSetting[];
}

/**
 * `shown` in groups, each group placed where its first setting of `all` sits,
 * so revealing the advanced settings never moves a group.
 */
export function groupSettings(all: ResolvedSetting[], shown: ResolvedSetting[] = all): SettingGroup[] {
  const order: string[] = [];
  for (const s of all) if (!order.includes(s.ui.group)) order.push(s.ui.group);
  for (const s of shown) if (!order.includes(s.ui.group)) order.push(s.ui.group);
  return order
    .map((title) => ({ title, settings: shown.filter((s) => s.ui.group === title) }))
    .filter((g) => g.settings.length > 0);
}

/**
 * What a form starts from, every setting: its value in `base` (a workspace's
 * own values, on its page), else its default, which the server has already
 * cascaded (a run starts from the workspace's values).
 */
export function initialValues(
  settings: Record<string, ActionSetting> | null | undefined,
  base?: Record<string, unknown> | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of resolveSettings(settings)) {
    out[s.key] = base && Object.prototype.hasOwnProperty.call(base, s.key) ? base[s.key] : s.default;
  }
  return out;
}

/** Only the values this mode may send: the server refuses any other key. */
export function editableValues(
  settings: Record<string, ActionSetting> | null | undefined,
  values: Record<string, unknown>,
  mode: SettingsMode,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of changeableSettings(settings, mode)) {
    if (Object.prototype.hasOwnProperty.call(values, s.key)) out[s.key] = values[s.key];
  }
  return out;
}

/**
 * The values that differ from the defaults above, for a workspace's entries:
 * a value left at its default is not stored, so a better default above (a new
 * version, a new configuration) reaches the workspace instead of the old one frozen
 * in place.
 */
export function changedValues(
  settings: Record<string, ActionSetting> | null | undefined,
  values: Record<string, unknown>,
  mode: SettingsMode,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of changeableSettings(settings, mode)) {
    if (!Object.prototype.hasOwnProperty.call(values, s.key)) continue;
    if (values[s.key] !== s.default) out[s.key] = values[s.key];
  }
  return out;
}
